import { type ReactNode } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../components/ui/Toast';
import {
  useCampaign,
  useCampaignSequence,
  useLaunchCampaign,
  type SequenceStep,
} from '../../api/campaigns.api';
import { useAudience } from '../../api/audiences.api';
import {
  FormCard,
  FormHeader,
  PrimaryButton,
  SecondaryButton,
  enrollmentMode,
  stepInfo,
  WizardLayout,
  stepPath,
} from './builder-shell';

/* ── Screen 16 — Campaign builder · Review and launch step ── */

const TRIGGER_LABELS: Record<string, string> = {
  application_stage_change: 'Job pipeline stage',
  deal_stage_change: 'Deal pipeline stage',
  application_stage_dwell: 'Job stage: in stage for N days',
  deal_stage_dwell: 'Deal stage: in stage for N days',
  meeting_booked: 'Meeting booked',
  list_added: 'Added to a list',
  new_placement: 'New placement',
  stage_change: 'Stage change',
};

export function CampaignReviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const { data: campaign, isLoading } = useCampaign(id);
  const { data: sequence } = useCampaignSequence(id);
  const { data: audience } = useAudience(campaign?.audience_id ?? undefined);
  const launch = useLaunchCampaign();

  if (!id) return <Navigate to="/campaigns/new" replace />;
  if (isLoading) return <LoadingShell />;
  if (!campaign) return <Navigate to="/campaigns" replace />;
  if (campaign.status !== 'DRAFT') return <Navigate to={`/campaigns/${id}`} replace />;

  const steps = (sequence?.steps ?? []) as SequenceStep[];
  const mode = enrollmentMode(campaign);
  const info = stepInfo('review', mode);

  const checks: { label: string; pass: boolean; hint?: string }[] = [
    { label: 'Name set', pass: !!campaign.name.trim() },
    mode === 'trigger'
      ? { label: 'Enrollment trigger configured', pass: !!campaign.trigger_config }
      : { label: 'Audience selected', pass: !!campaign.audience_id },
    { label: 'At least one step in sequence', pass: steps.length > 0 },
    {
      label: 'Email steps have subject + body',
      pass: steps.filter((s) => s.type === 'EMAIL').every((s) => (s.subject ?? '').trim() && ((s.body_text ?? '').trim() || s.template_id)),
      hint: steps.some((s) => s.type === 'EMAIL') ? undefined : 'No email steps in this sequence.',
    },
  ];

  const canLaunch = checks.every((c) => c.pass);

  const onLaunch = async () => {
    if (!canLaunch) return;
    try {
      await launch.mutateAsync(id);
      addToast({ variant: 'success', title: 'Campaign launched', description: `${campaign.name} is now live.` });
      navigate(`/campaigns/${id}`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Try again in a moment.';
      addToast({ variant: 'danger', title: 'Launch failed', description: msg });
    }
  };

  return (
    <WizardLayout step="review" campaignId={id} campaign={campaign}>
      <FormCard>
        <FormHeader stepIndex={info.index} totalSteps={info.total} label="Review and launch" />

        <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', color: 'var(--color-text-secondary)', lineHeight: 'var(--leading-relaxed)' }}>
          One last look. We&rsquo;ll check that everything is ready, then start enrolling contacts according to your
          send window.
        </p>

        <SummaryGrid>
          <SummaryItem label="Campaign" value={campaign.name} />
          <SummaryItem label="Type" value={campaign.type[0] + campaign.type.slice(1).toLowerCase()} />
          {mode === 'trigger' ? (
            <SummaryItem
              label="Enrollment"
              value={campaign.trigger_config ? `Trigger · ${TRIGGER_LABELS[campaign.trigger_config.type] ?? campaign.trigger_config.type}` : 'Trigger · not set'}
            />
          ) : (
            <SummaryItem label="Audience" value={audience ? `${audience.name} · ${audience.member_count.toLocaleString()} members` : '-'} />
          )}
          <SummaryItem label="Sequence" value={`${steps.length} ${steps.length === 1 ? 'step' : 'steps'}`} />
        </SummaryGrid>

        <ChecksList checks={checks} />

        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 'var(--space-5)', borderTop: '1px solid var(--color-border-default)' }}>
          <SecondaryButton onClick={() => navigate(stepPath(id, 'settings'))} disabled={launch.isPending}>
            Back
          </SecondaryButton>
          <PrimaryButton onClick={onLaunch} disabled={!canLaunch || launch.isPending}>
            {launch.isPending ? 'Launching…' : 'Launch campaign'}
          </PrimaryButton>
        </div>
      </FormCard>
    </WizardLayout>
  );
}

/* ── Summary ── */

function SummaryGrid({ children }: { children: ReactNode }) {
  return (
    <dl
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 'var(--space-3)',
        margin: 0,
      }}
    >
      {children}
    </dl>
  );
}

function SummaryItem({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        padding: 'var(--space-3) var(--space-4)',
        borderRadius: 'var(--radius-input)',
        backgroundColor: 'var(--color-surface-muted)',
        border: '1px solid var(--color-border-default)',
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
      }}
    >
      <dt style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)' }}>
        {label}
      </dt>
      <dd style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>
        {value}
      </dd>
    </div>
  );
}

/* ── Pre-launch checks ── */

function ChecksList({ checks }: { checks: { label: string; pass: boolean; hint?: string }[] }) {
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      {checks.map((c) => (
        <li
          key={c.label}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            padding: 'var(--space-3) var(--space-4)',
            borderRadius: 'var(--radius-input)',
            backgroundColor: c.pass ? 'var(--color-success-bg)' : 'var(--color-warning-bg)',
            border: `1px solid ${c.pass ? 'var(--color-success-border)' : 'var(--color-warning-border)'}`,
          }}
        >
          <span style={{ color: c.pass ? 'var(--color-success)' : 'var(--color-warning)' }}>
            {c.pass ? <IconCheck /> : <IconAlert />}
          </span>
          <span style={{ flex: 1, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>
            {c.label}
          </span>
          {c.hint && (
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
              {c.hint}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

function LoadingShell() {
  return (
    <WizardLayout step="review">
      <FormCard>
        <p style={{ fontFamily: 'var(--font-sans)', color: 'var(--color-text-muted)' }}>Loading campaign…</p>
      </FormCard>
    </WizardLayout>
  );
}

/* ── Icons ── */

function IconCheck() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function IconAlert() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
